/* eslint-disable consistent-return */
import axios from 'axios';
import { $i18n } from '@/provider/plugin';
import { useAuth } from '@/services/auth/useAuth';
import filters from '@/filters/filters';
import { isEmpty } from '@/helper/lodashHelper';
import localStorageHelper from '@/helper/localStorageHelper';
import { getLocale } from '@/plugins/i18n';
import { $settings } from '@/provider/settings';
import { useDebounceFn } from '@vueuse/core';
import { HTTP_STATUS } from '@/utils/httpStatusCodes';
import { clearAuth, setLoggedInBasic } from './services/auth/utils/authUtils';

const parseErrorCodes = (error) => {
  const status = Number.parseInt(error.response.status, 10);
  const bodyStatus = Number.parseInt(error.response.data.statusCode, 10);

  return { status, bodyStatus };
};

const isUnauthorizedError = (error) => {
  const { status, bodyStatus } = parseErrorCodes(error);

  if (status === HTTP_STATUS.UNAUTHORIZED) return true;
  if (bodyStatus === HTTP_STATUS.UNAUTHORIZED) return true;
  return false;
};

const isSapSessionAuthorized = async () => {
  try {
    const response = await axios.get(`${$settings.middlewareURI}/auths/check`, {
      withCredentials: true,
    });
    return !!response.data.authorized;
  } catch {
    return false;
  }
};

const debouncedAuthCheck = useDebounceFn(async (currentRoute) => {
  // invalidate Sessions
  if ($settings.useBasicAuth) {
    const isAuthorized = await isSapSessionAuthorized();
    if (!isAuthorized) {
      setLoggedInBasic(false);
    }
  } else {
    clearAuth();
  }

  // invalidated sessions -> routing to login -> re-authentication
  const pathSegments = currentRoute.path.split('/').filter(Boolean);
  const redirectPath = pathSegments.slice(2).join('/') || 'dashboard/home/';

  const { default: router } = await import('@/router');
  router.push({
    name: 'login',
    query: { redirect: redirectPath },
  });
}, 1000);

// TODO: IW-3970 This is a Quick Fix. Remove me asap, when ticket is done!
const modifyKey = (key, type) =>
  (type === 'contact' && key.split('/')[0]) || key;

const getLink = (key, type) => {
  if (key && type) {
    return {
      type,
      key: modifyKey(key, type),
      text: $i18n.global.t('WX_GENERAL_LINK_OPEN'),
    };
  }

  return false;
};

axios.interceptors.request.use(async (request) => {
  const auth = useAuth();
  if (
    (await auth.isAuthenticated()) &&
    request.url?.includes($settings.middlewareURI)
  ) {
    const cid = localStorageHelper.getCid();
    return auth.getToken().then((accessToken) => {
      request.withCredentials = true;
      request.headers.Authorization = `Bearer ${accessToken}`;
      request.headers['Access-Control-Allow-Origin'] = '*';
      // add SAP Information
      request.params = {
        ...request.params,
        'sap-language': getLocale(),
        'wx-cid': cid,
      };
      return request;
    });
  }

  return request;
});

axios.interceptors.response.use(
  (response) => {
    const { OBJKEY, OBJTYPE, SUCCESS, MESSAGES = [] } = response.data;

    // Handle only response from Middleware
    if (
      response.config.url.includes($settings.middlewareURI) && // Handle error with response 200 but Error Text in Body
      // if (String(response.status) === '401' || String(response.data.statusCode) === '401') {
      //   if ($settings.useBasicAuth === true) {
      //     const redirect = router.history.current.path || '/';
      //     router.push({ path: `/${I18nInstance.locale}/basicAuth`, query: { redirect } });
      //   }
      // }

      // Handle error by SAP Response
      response.data &&
      isEmpty(MESSAGES) === false
    ) {
      const uniqueMessages = MESSAGES.filter(
        (msg, index, arr) =>
          arr.findIndex((m) => m.TYPE === msg.TYPE && m.MESSAGE === msg.MESSAGE) === index,
      );
      const hasErrorWarningMessages = uniqueMessages.some((message) =>
        ['W', 'E'].includes(message.TYPE),
      );
      uniqueMessages.forEach((message) => {
        const { MESSAGE, TYPE } = message;

        if (['E'].includes(TYPE)) filters.showMessage('error', MESSAGE);

        if (['W'].includes(TYPE)) filters.showMessage('warn', MESSAGE);

        if (['S'].includes(TYPE))
          filters.showMessage('success', MESSAGE, getLink(OBJKEY, OBJTYPE));

        if (!hasErrorWarningMessages && ['I'].includes(TYPE))
          filters.showMessage('info', MESSAGE);
      });

      if (!SUCCESS) {
        const appLoading = document.querySelector('#loading-bg');
        if (appLoading) {
          appLoading.style.display = 'none';
        }
        console.warn(MESSAGES.filter(({ TYPE }) => TYPE === 'E'));
        console.warn(response);
        return Promise.reject(response);
      }
    }

    return response;
  },
  async (error) => {
    if (
      !(
        error.response &&
        error.response.config.url.includes($settings.middlewareURI)
      )
    )
      throw error;

    const { default: router } = await import('@/router');
    const currentRoute = router.currentRoute.value;
    const excludedPaths = ['/error/', '/login', '/basicAuth'];
    if (!excludedPaths.some((path) => currentRoute.path.includes(path)) &&
        isUnauthorizedError(error) &&
        error.config.url &&
        !error.config.url.includes('/auths/check')
      ) {
        const appLoading = document.querySelector('#loading-bg');
        if (appLoading) {
          appLoading.style.display = 'block';
        }
        debouncedAuthCheck(currentRoute);
      }

    throw error;
  },
);

axios.defaults.withCredentials = true;

export { default } from 'axios';
